import React, { useEffect, useState, useCallback } from 'react'
import { Container } from 'components'
import styles from './drivers.module.scss'
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material'
import { GridRowHeightParams } from '@mui/x-data-grid'
import {
  ActiveDriversChartData,
  ActiveVehiclesChartData,
  Driver,
  MappedDriver,
  VehicleAssignment,
  MappedVehicleAssignment,
  RecentTeamDriving,
  MappedRecentTeamDriving,
  AssignedDriver,
  TopDriverMileageGraphData,
  DriverMileage,
} from 'interfaces/IDrivers'
import { reportException } from 'tools/logs'
import { isCarrierLogin } from 'tools/auth'
import {
  getActiveDriversChartData,
  getActiveVehiclesChartData,
  getAverageMilesByDriver,
  getDriverList,
  getRecentTeamDriving,
  getRecentTeamDrivingChartData,
  getTopDriverMileageGraphData,
  getVehicleAssignments,
} from 'api'
import DriverListTable from './driverListTable'
import strings from 'strings'
import VehicleAssignmentsTable from './vehicleAssignmentsTable'
import RecentTeamDrivingTable from './recentTeamDrivingTable'
import { formatDateMMDDYYYY } from 'tools/helper'
import TopDriverGraph from './topDriverGraph'

const t = strings.TABLE_DEFAULTS
const e = strings.DRIVERS

interface IDriversDashboardProps {
  useKilometers?: boolean
}

const DriversDashboard: React.FC<IDriversDashboardProps> = ({ useKilometers = false }) => {
  const carrierId: any = isCarrierLogin()
    ? localStorage.getItem('companyId')
    : localStorage.getItem('carrierId') || '1'
  const distanceUnit = useKilometers ? 'kilometers' : 'miles'
  const distanceUnitShort = useKilometers ? 'kms' : 'miles'

  const [activeDriversChartData, setActiveDriversChartData] =
    useState<ActiveDriversChartData | null>(null)
  const [activeVehiclesChartData, setActiveVehiclesChartData] =
    useState<ActiveVehiclesChartData | null>(null)
  const [teamDrivingChartData, setTeamDrivingChartData] = useState<any>(null)
  const [averageMileageChartData, setAverageMileageChartData] =
    useState<number | null>(null)
  const [topDriverMileageGraphData, setTopDriverMileageGraphData] =
    useState<TopDriverMileageGraphData | null>(null)
  const [driverList, setDriverList] = useState<Driver[]>([])
  const [mappedDriverList, setMappedDriverList] = useState<MappedDriver[]>([])
  const [vehicleAssignments, setVehicleAssignments] = useState<
    VehicleAssignment[]
  >([])
  const [mappedVehicleAssignments, setMappedVehicleAssignments] =
    useState<MappedVehicleAssignment[]>([])
  const [recentTeamDrivingList, setRecentTeamDrivingList] =
    useState<RecentTeamDriving[]>([])
  const [mappedRecentTeamDrivingList, setMappedRecentTeamDrivingList] =
    useState<MappedRecentTeamDriving[]>([])

  const [isLoadingActiveDriversChartData, setIsLoadingActiveDriversChartData] =
    useState(false)
  const [
    isLoadingActiveVehiclesChartData,
    setIsLoadingActiveVehiclesChartData,
  ] = useState(false)
  const [
    isLoadingVehicleDriverRatioChartData,
    setIsLoadingVehicleDriverRatioChartData,
  ] = useState(false)
  const [isLoadingTeamDrivingChartData, setIsLoadingTeamDrivingChartData] =
    useState(false)
  const [
    isLoadingAverageMileageChartData,
    setIsLoadingAverageMileageChartData,
  ] = useState(false)
  const [
    isLoadingTopDriverMileageGraphData,
    setIsLoadingTopDriverMileageGraphData,
  ] = useState(false)
  const [isLoadingDriverList, setIsLoadingDriverList] = useState(false)
  const [isLoadingVehicleAssignments, setIsLoadingVehicleAssignments] =
    useState(false)
  const [isLoadingRecentTeamDrivingList, setIsLoadingRecentTeamDrivingList] =
    useState(false)

  const [hasActiveDriversChartData, setHasActiveDriversChartData] =
    useState(false)
  const [hasActiveVehiclesChartData, setHasActiveVehiclesChartData] =
    useState(false)
  const [hasTeamDrivingChartData, setHasTeamDrivingChartData] = useState(false)
  const [hasAverageMileageChartData, setHasAverageMileageChartData] =
    useState(false)
  const [hasTopDriverMileageGraphData, setHasTopDriverMileageGraphData] =
    useState(false)
  const [isLoadingInitial, setIsLoadingInitial] = useState(true)

  useEffect(() => {
    const loadKPIData = async () => {
      setIsLoadingInitial(true)
      try {
        await Promise.all([
          callGetActiveVehiclesChartData(),
          callGetActiveDriversChartData(),
          callGetAverageMilesByDriver(),
          callGetRecentTeamDrivingChartData(),
          callGetTopDriverMileageGraphData(),
        ])
      } finally {
        setIsLoadingInitial(false)
      }
    }

    const loadTableData = async () => {
      callGetDriverList()
      callGetVehicleAssignments()
      callGetRecentTeamDriving()
    }

    loadKPIData()
    loadTableData()
  }, [useKilometers])

  const callGetActiveDriversChartData = async () => {
    setIsLoadingActiveDriversChartData(true)
    try {
      const res = await getActiveDriversChartData(carrierId)
      if (res) {
        setActiveDriversChartData(res)
        setHasActiveDriversChartData(res.totalDrivers > 0)
      } else {
        setActiveDriversChartData(null)
        setHasActiveDriversChartData(false)
      }
    } catch (err) {
      reportException(err)
      setHasActiveDriversChartData(false)
    } finally {
      setIsLoadingActiveDriversChartData(false)
    }
  }

  const callGetActiveVehiclesChartData = async () => {
    setIsLoadingActiveVehiclesChartData(true)
    try {
      const res = await getActiveVehiclesChartData(carrierId)
      if (res) {
        setActiveVehiclesChartData(res)
        setHasActiveVehiclesChartData(res.totalVehicles > 0)
      } else {
        setActiveVehiclesChartData(null)
        setHasActiveVehiclesChartData(false)
      }
    } catch (err) {
      reportException(err)
      setHasActiveVehiclesChartData(false)
    } finally {
      setIsLoadingActiveVehiclesChartData(false)
    }
  }

  const callGetRecentTeamDrivingChartData = async () => {
    setIsLoadingTeamDrivingChartData(true)
    try {
      const res = await getRecentTeamDrivingChartData(carrierId)
      if (res !== null && res !== undefined) {
        setTeamDrivingChartData(res)
        setHasTeamDrivingChartData(true)
      } else {
        setTeamDrivingChartData(null)
        setHasTeamDrivingChartData(false)
      }
    } catch (err) {
      reportException(err)
    } finally {
      setIsLoadingTeamDrivingChartData(false)
    }
  }

  const callGetAverageMilesByDriver = async () => {
    setIsLoadingAverageMileageChartData(true)
    try {
      const res = await getAverageMilesByDriver(carrierId, useKilometers)
      if (res !== null && res !== undefined) {
        setAverageMileageChartData(res)
        setHasAverageMileageChartData(true)
      } else {
        setAverageMileageChartData(null)
        setHasAverageMileageChartData(false)
      }
    } catch (err) {
      reportException(err)
    } finally {
      setIsLoadingAverageMileageChartData(false)
    }
  }

  const callGetTopDriverMileageGraphData = async () => {
    setIsLoadingTopDriverMileageGraphData(true)
    try {
      const res = await getTopDriverMileageGraphData(carrierId, useKilometers)
      if (res) {
        setTopDriverMileageGraphData(res)
        const hasValidMileageData = res.totalDrivers > 0 && 
          res.drivers.some((driver: DriverMileage) => driver.miles > 0)
        setHasTopDriverMileageGraphData(hasValidMileageData)
      } else {
        setTopDriverMileageGraphData(null)
        setHasTopDriverMileageGraphData(false)
      }
    } catch (err) {
      reportException(err)
      setHasTopDriverMileageGraphData(false)
    } finally {
      setIsLoadingTopDriverMileageGraphData(false)
    }
  }

  const callGetDriverList = async () => {
    setIsLoadingDriverList(true)
    try {
      const res = await getDriverList(carrierId, useKilometers)
      if (res) {
        setDriverList(res)
        setMappedDriverList(mapDriverListData(res))
      } else {
        setDriverList([])
        setMappedDriverList([])
      }
    } catch (err) {
      reportException(err)
    } finally {
      setIsLoadingDriverList(false)
    }
  }

  const callGetVehicleAssignments = async () => {
    setIsLoadingVehicleAssignments(true)
    try {
      const res = await getVehicleAssignments(carrierId, useKilometers)
      if (res) {
        setVehicleAssignments(res)
        setMappedVehicleAssignments(mapVehicleAssignmentsData(res))
      } else {
        setVehicleAssignments([])
        setMappedVehicleAssignments([])
      }
    } catch (err) {
      reportException(err)
    } finally {
      setIsLoadingVehicleAssignments(false)
    }
  }

  const callGetRecentTeamDriving = async () => {
    setIsLoadingRecentTeamDrivingList(true)
    try {
      const res = await getRecentTeamDriving(carrierId, useKilometers)
      if (res) {
        setRecentTeamDrivingList(res)
        setMappedRecentTeamDrivingList(mapRecentTeamDrivingData(res))
      } else {
        setRecentTeamDrivingList([])
        setMappedRecentTeamDrivingList([])
      }
    } catch (err) {
      reportException(err)
    } finally {
      setIsLoadingRecentTeamDrivingList(false)
    }
  }

  const calculateDriverVehicleRatio = () => {
    if (
      activeDriversChartData?.activeDrivers &&
      activeDriversChartData.activeDrivers > 0 &&
      activeVehiclesChartData?.activeVehicles &&
      activeVehiclesChartData.activeVehicles > 0
    ) {
      return (
        activeDriversChartData.activeDrivers /
        activeVehiclesChartData.activeVehicles
      ).toFixed(2)
    }
    return '0.00'
  }

  const mapAssignedDrivers = (drivers: AssignedDriver[]): AssignedDriver[] => {
    return drivers.map((driver) => ({
      driverName: driver.driverName || t.DASH_VALUE,
      eldDriverId: driver.eldDriverId || t.DASH_VALUE,
    }))
  }

  const truncateDriverDisplay = useCallback(
    (name: string, id: string, maxLength: number = 30) => {
      const idWithParens = `(${id})`
      const totalLength = name.length + idWithParens.length + 1

      if (totalLength <= maxLength) {
        return { name, id: idWithParens }
      }

      const minIdSpace = 8
      const maxIdLength = Math.min(
        idWithParens.length,
        Math.max(minIdSpace, maxLength * 0.3)
      )
      const remainingSpace = maxLength - maxIdLength - 1

      const truncatedName =
        remainingSpace > 3
          ? name.substring(0, Math.max(3, remainingSpace - 3)) + '...'
          : name.substring(0, Math.max(1, remainingSpace))

      const truncatedId =
        idWithParens.length > maxIdLength
          ? '(' + id.substring(0, Math.max(1, maxIdLength - 5)) + '...)'
          : idWithParens

      return { name: truncatedName, id: truncatedId }
    },
    []
  )

  const createGetEstimatedRowHeight = useCallback(
    (driversPropertyName: string) => 
      ({ model, densityFactor }: GridRowHeightParams): number | null => {
        const drivers = model?.[driversPropertyName]

        if (drivers && Array.isArray(drivers)) {
          const baseHeight = t.DEFAULT_ROW_HEIGHT
          const driverCount = drivers.length
          const heightPerDriver = t.DEFAULT_DRIVER_ROW_HEIGHT
          const padding = t.DEFAULT_CELL_PADDING

          const calculatedHeight = driverCount * heightPerDriver + padding
          return Math.max(baseHeight, calculatedHeight) * densityFactor
        }
        return null
      },
    [mappedVehicleAssignments, mappedRecentTeamDrivingList]
  )

  const mapDriverListData = (drivers: Driver[]): MappedDriver[] => {
    return drivers.map((driver) => ({
      rowId: driver.rowId,
      eldDriverId: driver.eldDriverId || t.DASH_VALUE,
      driverName: driver.driverName || t.DASH_VALUE,
      licenseNo: driver.licenseNo || t.DASH_VALUE,
      email: driver.email || t.DASH_VALUE,
      miles: driver.miles.toString() || t.DASH_VALUE,
      lastKnownVehicle: driver.lastKnownVehicle || t.DASH_VALUE,
    }))
  }

  const mapVehicleAssignmentsData = (
    vehicles: VehicleAssignment[]
  ): MappedVehicleAssignment[] => {
    return vehicles.map((vehicle) => ({
      rowId: vehicle.rowId,
      model: vehicle.model || t.DASH_VALUE,
      plateNumber: vehicle.plateNumber || t.DASH_VALUE,
      status: vehicle.status || false,
      miles: vehicle.miles.toString() || t.DASH_VALUE,
      teamVehicle: vehicle.teamVehicle || false,
      assignedDrivers: vehicle.assignedDrivers ? mapAssignedDrivers(vehicle.assignedDrivers) : [],
      vin: vehicle.vin || t.DASH_VALUE,
    }))
  }

  const mapRecentTeamDrivingData = (
    recentTeamDriving: RecentTeamDriving[]
  ): MappedRecentTeamDriving[] => {
    return recentTeamDriving.map((recentTeamDriving) => ({
      rowId: recentTeamDriving.rowId,
      numDrivers: recentTeamDriving.numDrivers || 0,
      date: formatDateMMDDYYYY(recentTeamDriving.date) || t.DASH_VALUE,
      assetDisplayName: recentTeamDriving.assetDisplayName || t.DASH_VALUE,
      plateNumber: recentTeamDriving.plateNumber || t.DASH_VALUE,
      vin: recentTeamDriving.vin || t.DASH_VALUE,
      milesThatDay: recentTeamDriving.milesThatDay || t.DASH_VALUE,
      drivers: recentTeamDriving.drivers ? mapAssignedDrivers(recentTeamDriving.drivers) : [],
    }))
  }

  return (
    <Container className={styles.driversContainer}>
      {isLoadingInitial ? (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid #E9E9E9',
            borderRadius: '14px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={50} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Loading Drivers Dashboard Data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the initial data
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Metrics Cards Section */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={2.4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9E9E9',
                  borderRadius: '14px',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div className={styles.metricLabel}>{e.ACTIVE_DRIVERS}</div>
                {isLoadingActiveDriversChartData ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasActiveDriversChartData &&
                  activeDriversChartData?.totalDrivers ? (
                  <>
                    <div
                      className={styles.metricValue}
                      style={{
                        color: '#0088E1',
                        fontSize: '2.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {activeDriversChartData.activeDrivers}
                    </div>
                    <div className={styles.metricSubtitle}>
                      Out of {activeDriversChartData?.totalDrivers || 0} total
                      drivers
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.metricSubtitle}
                    style={{ color: '#0088E1', fontSize: '1.5rem' }}
                  >
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9E9E9',
                  borderRadius: '14px',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div className={styles.metricLabel}>{e.ACTIVE_VEHICLES}</div>
                {isLoadingActiveVehiclesChartData ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasActiveVehiclesChartData &&
                  activeVehiclesChartData?.activeVehicles ? (
                  <>
                    <div
                      className={styles.metricValue}
                      style={{
                        color: '#0088E1',
                        fontSize: '2.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {activeVehiclesChartData.activeVehicles}
                    </div>
                    <div className={styles.metricSubtitle}>
                      Out of {activeVehiclesChartData?.totalVehicles || 0} total
                      vehicles
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.metricSubtitle}
                    style={{ color: '#0088E1', fontSize: '1.5rem' }}
                  >
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9E9E9',
                  borderRadius: '14px',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div className={styles.metricLabel}>
                  {e.ACTIVE_DRIVER_VEHICLE_RATIO}
                </div>
                {isLoadingVehicleDriverRatioChartData ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasActiveVehiclesChartData &&
                  activeVehiclesChartData?.activeVehicles ? (
                  <>
                    <div
                      className={styles.metricValue}
                      style={{
                        color: '#0088E1',
                        fontSize: '2.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {calculateDriverVehicleRatio()}
                    </div>
                    <div className={styles.metricSubtitle}>
                      Active drivers per active vehicle
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.metricSubtitle}
                    style={{ color: '#0088E1', fontSize: '1.5rem' }}
                  >
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9E9E9',
                  borderRadius: '14px',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div className={styles.metricLabel}>Team Driving Instances</div>
                {isLoadingTeamDrivingChartData ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasTeamDrivingChartData && teamDrivingChartData ? (
                  <>
                    <div
                      className={styles.metricValue}
                      style={{
                        color: '#0088E1',
                        fontSize: '2.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {teamDrivingChartData?.teamDriverInstances || 0}
                    </div>
                    <div className={styles.metricSubtitle}>
                      In the last 30 days
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.metricSubtitle}
                    style={{ color: '#0088E1', fontSize: '1.5rem' }}
                  >
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid #E9E9E9',
                  borderRadius: '14px',
                  height: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div className={styles.metricLabel}>
                  {useKilometers ? `Average ${distanceUnit.charAt(0).toUpperCase() + distanceUnit.slice(1)} by Driver` : e.AVERAGE_MILES_BY_DRIVER}
                </div>
                {isLoadingAverageMileageChartData ? (
                  <CircularProgress size={30} sx={{ my: 2 }} />
                ) : hasAverageMileageChartData &&
                  averageMileageChartData !== null ? (
                  <>
                    <div
                      className={styles.metricValue}
                      style={{
                        color: '#0088E1',
                        fontSize: '2.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {Math.trunc(
                        Math.abs(averageMileageChartData || 0)
                      ).toLocaleString()}
                    </div>
                    <div className={styles.metricSubtitle}>{distanceUnitShort} / 30 days</div>
                  </>
                ) : (
                  <div
                    className={styles.metricSubtitle}
                    style={{ color: '#0088E1', fontSize: '1.5rem' }}
                  >
                    No data available
                  </div>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Top Driver Mileage Graph */}
          <Box sx={{ mb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E9E9E9',
                borderRadius: '14px',
                height: '450px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {useKilometers ? `Top Driver ${distanceUnit.charAt(0).toUpperCase() + distanceUnit.slice(1)} (30 Days)` : 'Top Driver Mileage (30 Days)'}
              </Typography>
              <Box sx={{ flex: 1, position: 'relative' }}>
                {isLoadingTopDriverMileageGraphData ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading chart data...
                    </Typography>
                  </Box>
                ) : hasTopDriverMileageGraphData && topDriverMileageGraphData ? (
                  <TopDriverGraph data={topDriverMileageGraphData} useKilometers={useKilometers} />
                ) : topDriverMileageGraphData && topDriverMileageGraphData.totalDrivers > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                      No {distanceUnit} data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All drivers have 0 {distanceUnitShort} in the last 30 days
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No driver data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Driver List Table */}
          <Box sx={{ mb: 3 }}>
            <DriverListTable
              loading={isLoadingDriverList}
              data={mappedDriverList}
              useKilometers={useKilometers}
            />
          </Box>

          {/* Vehicle Assignments Table */}
          <Box sx={{ mb: 3 }}>
            <VehicleAssignmentsTable
              loading={isLoadingVehicleAssignments}
              data={mappedVehicleAssignments}
              truncateDriverDisplay={truncateDriverDisplay}
              getEstimatedRowHeight={createGetEstimatedRowHeight(
                e.VEHICLE_ASSIGNMENTS_DRIVERS_PROPERTY
              )}
              useKilometers={useKilometers}
            />
          </Box>

          {/* Recent Team Driving Table */}
          <Box sx={{ mb: 3 }}>
            <RecentTeamDrivingTable
              loading={isLoadingRecentTeamDrivingList}
              data={mappedRecentTeamDrivingList}
              truncateDriverDisplay={truncateDriverDisplay}
              getEstimatedRowHeight={createGetEstimatedRowHeight(
                e.RECENT_TEAM_DRIVING_DRIVERS_PROPERTY
              )}
              useKilometers={useKilometers}
            />
          </Box>
        </>
      )}
    </Container>
  )
}

export default DriversDashboard
